import { useCallback, useEffect, useState } from "react";
import type { ICompanyOptions } from "../../../../CompanyBuilder/types/companyList";
import useCompanyOptions from "../../../../../customHooks/apps/useCompanyOptions";

const COMPANY_SEARCH_TYPE = "Current Company By ID";
const MIN_COMPANY_SEARCH_LENGTH = 2;
const COMPANY_SEARCH_DELAY_MS = 500;

interface IUseProjectCompanyOptionsArgs {
  visible: boolean;
}

const getProjectCompanyOptionLabel = ({
  option,
}: {
  option: ICompanyOptions;
}): string => option.name || option.label;

const toProjectCompanyOption = ({
  option,
}: {
  option: ICompanyOptions;
}): ICompanyOptions => ({
  ...option,
  label: getProjectCompanyOptionLabel({ option }),
});

export const useProjectCompanyOptions = ({
  visible,
}: IUseProjectCompanyOptionsArgs) => {
  const { loadOptions } = useCompanyOptions();
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<ICompanyOptions[]>([]);
  const [searchValue, setSearchValue] = useState("");

  const resetOptions = useCallback(() => {
    setIsLoading(false);
    setOptions([]);
    setSearchValue("");
  }, []);

  const searchCompanies = useCallback(
    ({ searchValue }: { searchValue: string }) => {
      setSearchValue(searchValue);
    },
    [],
  );

  useEffect(() => {
    if (!visible) {
      resetOptions();
    }
  }, [resetOptions, visible]);

  useEffect(() => {
    const trimmedSearchValue = searchValue.trim();

    if (!visible || trimmedSearchValue.length < MIN_COMPANY_SEARCH_LENGTH) {
      setIsLoading(false);
      setOptions([]);
      return undefined;
    }

    let isActive = true;
    setIsLoading(true);

    const timeout = setTimeout(async () => {
      try {
        const results = await loadOptions(trimmedSearchValue, COMPANY_SEARCH_TYPE);

        if (isActive) {
          setOptions(results.map((option) => toProjectCompanyOption({ option })));
        }
      } catch {
        if (isActive) {
          setOptions([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }, COMPANY_SEARCH_DELAY_MS);

    return () => {
      isActive = false;
      clearTimeout(timeout);
    };
  }, [loadOptions, searchValue, visible]);

  return {
    isLoading,
    options,
    resetOptions,
    searchCompanies,
  };
};
